"use client";

import { useEffect, useRef } from "react";

interface TVOverlayProps {
  className?: string;
  intensity?: "light" | "medium" | "heavy";
}

export function TVOverlay({ className = "", intensity = "medium" }: TVOverlayProps) {
  const animationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Use CSS custom properties for dynamic values
    if (animationRef.current) {
      const duration = intensity === "heavy" ? "3s" : intensity === "medium" ? "5s" : "8s";
      animationRef.current.style.setProperty("--scan-duration", duration);
    }
  }, [intensity]);

  const opacityMap = {
    light: 0.015,
    medium: 0.025,
    heavy: 0.04,
  };

  const vignette = {
    light: 0.05,
    medium: 0.1,
    heavy: 0.15,
  };

  return (
    <div
      ref={animationRef}
      className={`pointer-events-none absolute inset-0 z-50 overflow-hidden ${className}`}
      style={{
        // Use will-change to hint browser about animations
        willChange: "contents",
      }}
    >
      {/* Scanlines - Using CSS for hardware acceleration */}
      <div
        className="tv-scanlines absolute inset-0"
        style={{
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(255, 255, 255, ${opacityMap[intensity]}) 2px,
            rgba(255, 255, 255, ${opacityMap[intensity]}) 3px
          )`,
          backgroundSize: "100% 3px",
          mixBlendMode: "screen",
        }}
      />

      {/* Static noise - Simplified for performance */}
      <div
        className="tv-static absolute inset-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E")`,
          backgroundSize: "100px 100px",
          opacity: opacityMap[intensity] * 0.5,
          mixBlendMode: "overlay",
        }}
      />

      {/* Flicker effect - Using CSS animation */}
      <div
        className="tv-flicker absolute inset-0"
        style={{
          animation: "tvFlicker 4s infinite",
          opacity: 0,
        }}
      />

      {/* CRT vignette */}
      <div
        className="tv-vignette absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at center, transparent 0%, transparent 50%, rgba(0, 0, 0, ${vignette[intensity]}) 100%)`,
        }}
      />

      {/* Scan line - Using CSS animation for hardware acceleration */}
      <div
        className="tv-scan-line absolute inset-x-0 h-[3px]"
        style={{
          background: `linear-gradient(to bottom, transparent, rgba(255, 255, 255, ${opacityMap[intensity]}), transparent)`,
          animation: `tvScanLine var(--scan-duration, 5s) linear infinite`,
          transform: "translateZ(0)", // Force GPU acceleration
          boxShadow: `0 0 4px rgba(255, 255, 255, ${opacityMap[intensity] * 0.5})`,
        }}
      />

      <style jsx>{`
        @keyframes tvFlicker {
          0%,
          100% {
            opacity: 0;
          }
          92% {
            opacity: 0;
          }
          93% {
            opacity: 0.02;
          }
          94% {
            opacity: 0;
          }
        }

        @keyframes tvScanLine {
          0% {
            transform: translateY(-2px) translateZ(0);
          }
          100% {
            transform: translateY(100vh) translateZ(0);
          }
        }

        /* Reduce motion for accessibility */
        @media (prefers-reduced-motion: reduce) {
          .tv-scan-line {
            animation: none !important;
          }
          .tv-flicker {
            animation: tvFlickerReduced 8s infinite !important;
          }
        }

        @keyframes tvFlickerReduced {
          0%,
          100% {
            opacity: 0;
          }
          50% {
            opacity: 0.02;
          }
        }
      `}</style>
    </div>
  );
}
